import { animate, createScope, stagger, onScroll } from '../vendor/anime.esm.js';
import { DURATION, EASE, STAGGER, SPRING_PRESETS, resolveDuration, resolveStagger, IS_TOUCH, PREFERS_REDUCED_MOTION, scrollTrigger } from './motion-tokens.js';
import { getVariantAnimation, setMotionComplexity } from './variant-animations.js';
import { registerPostRender } from '../core/render.js';
import { store } from '../core/store.js';
import { esDecoracion } from './fondos/decoracion.js';

let globalScope = null;
let variantReverts = [];
// IntersectionObservers y timers del rescate creados en el último initGlobalAnimations().
// Se guardan para poder soltarlos en destroyGlobalAnimations(): al re-renderizar (router SPA
// por página) los nodos observados quedan desprendidos y, sin esto, seguirían observados
// (fuga) y sus timers marcarían .anime-init sobre DOM que ya no está en la página.
let blockObservers = [];
let rescueTimers = [];

// Selectores para aplicar stagger automático (lista definida por el Director de Diseño)
const STAGGER_TARGETS = '.card, .feature-card, .stat-item, .faq-item, .plan-card, .timeline-event, .team-card, .process-step, .gallery-item';

// Selector DRY de textos animables (Capa 2 del fallback cinematográfico). Compartido por
// animateBlockCinematic y por la red de seguridad anti-invisibilidad del rescate, para que
// ambos recorran EXACTAMENTE los mismos targets y no se desincronicen.
const TEXT_TARGETS_SELECTOR = 'h1, h2, h3, p, .subtitle, .badge, .card, .feature-card, .plan-card, .timeline-event, .faq-item, .stat-item, .gallery-item, .team-card';

/**
 * Mapeo de tipos de animación (data-anim) a sus respectivas configuraciones.
 * Retorna las opciones listas para pasar a `animate()`.
 */
const getAnimConfig = (type, isAboveFold, hasSplide) => {
    const dur = resolveDuration(isAboveFold ? DURATION.fast : DURATION.slow);
    const blurStart = isAboveFold ? 'blur(2px)' : 'blur(4px)';
    
    // Fallback de seguridad si Splide está presente y se intenta un scale/rotateX
    if (hasSplide) {
        if (type === 'zoom-in') type = 'fade-up';
        if (type === 'zoom-out') type = 'fade-down';
        if (type === 'flip-up') type = 'slide-up';
    }

    const base = {
        opacity: [0, 1],
        filter: [blurStart, 'blur(0px)']
    };

    switch (type) {
        case 'fade-up':
            return { ...base, translateY: [40, 0], duration: dur, ease: EASE.refined, delay: stagger(resolveStagger(STAGGER.normal)) };
        case 'fade-down':
            return { ...base, translateY: [-40, 0], duration: dur, ease: EASE.refined, delay: stagger(resolveStagger(STAGGER.normal)) };
        case 'fade-left':
            return { ...base, translateX: [-60, 0], duration: dur, ease: EASE.snappy, delay: stagger(resolveStagger(STAGGER.fast)) };
        case 'fade-right':
            return { ...base, translateX: [60, 0], duration: dur, ease: EASE.snappy, delay: stagger(resolveStagger(STAGGER.fast)) };
        case 'zoom-in':
            return { ...base, scale: [0.85, 1], duration: dur, ease: EASE.snappy, delay: stagger(resolveStagger(STAGGER.normal)) };
        case 'zoom-out':
            return { ...base, scale: [1.1, 1], duration: dur, ease: EASE.snappy, delay: stagger(resolveStagger(STAGGER.normal)) };
        case 'slide-up':
            return { ...base, translateY: [60, 0], duration: dur, ease: EASE.bouncy, delay: stagger(resolveStagger(STAGGER.normal)) };
        case 'flip-up':
            return { ...base, rotateX: [-15, 0], duration: dur, ease: EASE.organic, delay: stagger(resolveStagger(STAGGER.slow)) };
        default:
            return null; // Fallback
    }
};

export function destroyGlobalAnimations() {
    blockObservers.forEach(obs => {
        try {
            obs.disconnect();
        } catch (e) {
            console.warn('Error disconnecting block observer:', e);
        }
    });
    blockObservers = [];
    rescueTimers.forEach(t => clearTimeout(t));
    rescueTimers = [];
    variantReverts.forEach(fn => {
        try {
            if (typeof fn === 'function') fn();
        } catch (e) {
            console.warn('Error in custom variant revert:', e);
        }
    });
    variantReverts = [];
    if (globalScope) {
        try {
            globalScope.revert();
        } catch (e) {
            console.warn('Error in globalScope revert:', e);
        }
        globalScope = null;
    }
}

// Margen (ms) desde que un bloque DISPARA su animación hasta que corre su red de seguridad.
// DEBE ser mayor que la coreografía más larga: el rescate limpia el inline que anime escribió,
// así que si dispara con un tween aún vivo le roba el estado y provoca un salto de 1 frame.
// No se puede deducir del código (los `ease: SPRING_PRESETS.*` ignoran el `duration` declarado
// y calculan el suyo propio); medido en Chrome sobre el peor caso real (hero con título largo
// + subtítulo + CTA con muelle `magnetic`): ~5.0 s. 6 s deja margen sin quedarse corto.
const RESCUE_DELAY = 6000;

/** ¿El visitante está viendo este bloque AHORA MISMO? (viewport real, no el recortado por el
 *  rootMargin del IntersectionObserver). Gobierna el revelado del rescate: solo se adelanta un
 *  bloque que ya está en pantalla, nunca uno al que el visitante aún no ha llegado. */
function isInViewport(el) {
    const r = el.getBoundingClientRect();
    return r.height > 0 && r.top < window.innerHeight && r.bottom > 0;
}

/**
 * Red de seguridad anti-invisibilidad de UN bloque, anclada al DISPARO de su animación
 * (no a la carga de la página). Un solo camino que sirve igual al bloque above-fold que a
 * los que animan al hacer scroll.
 *
 * Antes era un único setTimeout global que recorría TODOS los bloques a los 1.5 s. Eso:
 *   a) revelaba (.anime-init → CSS opacity:1) bloques que el visitante aún no había
 *      alcanzado; al llegar con el scroll, su animación arrancaba en opacity:0 → el bloque
 *      estaba visible y se apagaba = parpadeo; y
 *   b) corría UNA sola vez, así que los bloques que animan al hacer scroll (la mayoría de
 *      la página) se quedaban sin red: si uno se congelaba, el rescate ya había pasado.
 * Anclarlo al disparo arregla las dos cosas: nunca adelanta un bloque no visto y cubre el
 * scroll, porque cada bloque trae su propia red cuando le toca animar.
 */
/**
 * Deja un bloque VISIBLE y sin animación, ya.
 *
 * Se usa cuando la coreografía de una variante lanza: casi todas empiezan poniendo
 * `opacity:0` (+ filter/transform) inline en sus piezas y animan DESPUÉS, así que un fallo a
 * medias deja el bloque escrito en opacity 0 — invisible hasta que la red de rescate lo limpia
 * a los 6 s. Eso es justo lo que se ve como "no anima y de pronto aparece de golpe".
 *
 * Solo se tocan los elementos con `opacity` INLINE menor que 1: es la firma de una coreografía
 * a medio aplicar. Las opacidades autoradas por CSS y la imagen de Unsplash (que nace en 0 a
 * propósito y se revela al cargar) se dejan en paz.
 */
function revelarSinAnimacion(block) {
    block.classList.add('anime-init');
    // Nodos que una coreografía ESCONDIÓ para montar su propio andamio (hoy solo la timeline
    // sticky, que sustituye la sección entera por un panel `position:sticky`). Limpiar
    // `opacity` no basta: un `display:none` que nadie deshace deja la sección MEDIO construida
    // —una franja vacía donde estaba el contenido— y ninguna otra red lo destapa. La marca la
    // pone quien esconde (`data-anim-oculto`, ver animations/variants/timeline.js).
    block.querySelectorAll('[data-anim-oculto]').forEach(el => {
        el.style.display = '';
        el.removeAttribute('data-anim-oculto');
    });
    block.querySelectorAll('[style*="opacity"]').forEach(el => {
        if (el.classList.contains('unsplash-target')) return;
        if (parseFloat(el.style.opacity) < 1) {
            el.style.opacity = '';
            el.style.filter = '';
            el.style.transform = '';
        }
    });
}

function scheduleAntiInvisibilityRescue(block) {
    rescueTimers.push(setTimeout(() => {
        // 1) Si el bloque sigue sin anime-init (p.ej. su variantFn lanzó antes del rAF que
        //    lo marca, o su IO nunca disparó), forzar su revelado: el CSS lo tiene a opacity:0.
        //    SOLO si el visitante lo está viendo AHORA: revelar un bloque que aún no ha
        //    alcanzado es lo que causaba el parpadeo (al llegar con el scroll, su animación
        //    arrancaba en opacity:0 → el bloque estaba visible y se apagaba).
        if (!block.classList.contains('anime-init') && isInViewport(block)) {
            block.classList.add('anime-init');
        }
        // 2) Rescatar textos que un tween SOLAPADO dejó congelados semi-invisibles (opacity < 1
        //    con blur/transform inline). Se limpia SOLO el inline que anime escribió
        //    (opacity/filter/transform) → el texto recupera su valor autorado/CSS visible.
        //    NO se fuerza opacity:1 a ciegas: un <p> con solo opacity autorada (p.ej. 0.85, sin
        //    filter/transform inline) NO se toca, así se respetan las opacidades intencionales
        //    del diseño.
        block.querySelectorAll(TEXT_TARGETS_SELECTOR).forEach(el => {
            // Firma de un tween de anime = filter/transform escritos inline.
            const animWroteInline = el.style.filter !== '' || el.style.transform !== '';
            if (animWroteInline && parseFloat(getComputedStyle(el).opacity) < 1) {
                el.style.opacity = '';
                el.style.filter = '';
                el.style.transform = '';
            }
        });
    }, RESCUE_DELAY));
}

/**
 * Helper DRY: ejecuta `onEnter` la PRIMERA vez que el bloque entra en viewport.
 * Centraliza el IntersectionObserver que antes estaba duplicado (variante y fallback) y
 * registra el observer para que destroyGlobalAnimations() pueda desconectarlo.
 */
function observeOnce(block, onEnter) {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                observer.unobserve(block);
                onEnter();
            }
        });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
    blockObservers.push(observer);
    observer.observe(block);
}

/** Helper DRY: anima ghost-numbers de un bloque */
function animateGhostNumbers(block) {
    const ghostNumbers = block.querySelectorAll('.ghost-number');
    if (!ghostNumbers.length) return;
    ghostNumbers.forEach((gn, i) => {
        const targetOpacity = parseFloat(gn.style.opacity) || 0.04;
        animate(gn, {
            opacity: [0, targetOpacity],
            duration: 1000,
            delay: 200 + (i * 80),
            ease: 'outQuart'
        });
    });
}

/**
 * Tipos de bloque que entran SIN animación de entrada en TODAS las superficies (publicado, SPA
 * pública y «Ver sitio»). Lista de CAPACIDAD (5.4 bis): va con su gemela
 * `editor/section-settings.js::SECTION_SETTINGS_EXCLUDE`, que les quita el panel —la única
 * puerta a `props.animation`—, así que su animación nunca es una ELECCIÓN del cliente.
 *
 * `ai-custom`: su `fade-up` del publicado es SIEMPRE el valor por defecto (lo ponen
 * `compileSiteHtml` y el shim a falta de prop), mientras que el editor y la SPA ya lo revelan sin
 * animar (`render.js` emite `data-anim=""`). Y no era inocuo: el fallback cinematográfico deja
 * `filter: blur(0px)` + `transform` INLINE para siempre en la sección del iframe (medido: 26 FPS
 * con ellos, 48 quitándolos en caliente). Esto es paridad, no diseño, y no exige republicar.
 */
export const TIPOS_SIN_ENTRADA = ['ai-custom'];

/** Revela un bloque YA, sin animar: `anime-init` (el CSS sube la opacidad de la sección) y fuera
 *  el opacity/filter inline que hubiera dejado un render previo. Es el revelado que repetían las
 *  ramas `'none'` y `!animType` (y el fast-path del editor): una sola copia. No confundir con
 *  `revelarSinAnimacion`, que además deshace el andamio de una coreografía que lanzó a medias. */
function revelarSinEntrada(block) {
    block.classList.add('anime-init');
    block.style.opacity = '1';
    block.style.filter = 'none';
}

export function initGlobalAnimations() {
    destroyGlobalAnimations();

    const root = document.getElementById('resultado');
    if (!root) return;

    // Fast-path: Si es modo editor o movimiento reducido, forzar todo visible inmediatamente.
    // (`anime-init` libera el fallback CSS.)
    if (store.get('editorMode') || PREFERS_REDUCED_MOTION) {
        root.querySelectorAll('[data-anim]').forEach(revelarSinEntrada);
        return;
    }

    // Durante generación, typewrite.js controla la visibilidad de cada bloque.
    // NO tocar nada aquí para evitar flash de contenido completo.
    if (store.get('isGenerating')) return;

    globalScope = createScope({ root });
    const blocks = root.querySelectorAll('[data-anim]');

    // --- MOTION COMPLEXITY: lee el tono del DOM y lo propaga al registry ---
    // 'formal' o presencia de tono en DOM = reduced (abogados, clínicas, corporativo)
    // El selector #tono solo existe en editor — en viewer se asume 'full'
    const tonoEl = document.getElementById('tono');
    const tono   = tonoEl?.value || 'persuasivo';
    const motionComplexity = tono === 'formal' ? 'reduced' : 'full';
    setMotionComplexity(motionComplexity);

    blocks.forEach((block, index) => {
        const animType = block.getAttribute('data-anim');
        const isAboveFold = index === 0;
        const blockType = block.dataset.type || '';

        // Header no necesita animación cinematográfica — solo revelar
        if (blockType === 'header') {
            block.classList.add('anime-init');
            const headerEl = block.querySelector('header');
            if (headerEl) animate(headerEl, { opacity: [0, 1], duration: resolveDuration(DURATION.normal), ease: EASE.refined });
            return;
        }

        // Tipos que entran sin animación (hoy el bloque IA): ANTES del lookup de variante y del
        // fallback cinematográfico, que es quien dejaba el blur/transform inline en su sección.
        if (TIPOS_SIN_ENTRADA.includes(blockType)) {
            revelarSinEntrada(block);
            return;
        }

        const hasSplide = block.querySelector('.splide') !== null;

        // --- "Ninguna" (props.animation === 'none') gana a TODO ---
        // Va ANTES del lookup de variante a propósito: la rama variantFn hace `return`,
        // así que si esta comprobación viviera con el fallback genérico (más abajo) la
        // prop sería INERTE para todo bloque con coreografía propia (form, reservas,
        // trust, reviews, hero, features…) y el cliente NO podría desactivar su
        // animación desde "Ajustes de sección" → "Animación de entrada".
        // Solo se honra el 'none' EXPLÍCITO: sin prop, render.js emite data-anim=""
        // (no "none"), que cae al lookup de variante como siempre.
        // Revelar sin animar = añadir anime-init (el CSS sube la opacity del > section)
        // y limpiar el opacity/filter inline por si un render previo los dejó puestos.
        if (animType === 'none') {
            revelarSinEntrada(block);
            return;
        }

        // --- Lookup de variante ANTES del fallback genérico ---
        const variantClass = block.querySelector('[class*="variant-v"]');
        const variant = variantClass
            ? (variantClass.className.match(/variant-(v\d)/) || [])[1] || 'v1'
            : 'v1';
        const variantFn = getVariantAnimation(blockType, variant);

        if (variantFn) {
            const runVariant = () => {
                // try/catch a propósito: anime.js NO captura lo que lance la coreografía
                // (Scope.execute llama `cb(this)` a pelo), así que una variante rota se
                // llevaba por delante su bloque de dos formas:
                //   · below-fold: la excepción impedía llegar al rAF que añade `.anime-init`
                //     → el CSS lo mantenía a opacity:0;
                //   · above-fold: `.anime-init` sí estaba, pero la coreografía ya había
                //     escrito `opacity:0` inline en sus piezas antes de fallar, y el inline
                //     gana al CSS.
                // En los dos casos el bloque quedaba invisible hasta que la red de rescate
                // lo destapaba a los 6 s. Ahora el fallo degrada a "sin animación" al
                // instante, y el aviso dice QUÉ variante hay que mirar.
                try {
                    const result = variantFn(block);
                    if (result?.revert && result.isCustomTimelineCleanup) {
                        variantReverts.push(result.revert);
                    }
                } catch (e) {
                    console.warn(
                        '[anim] la coreografía de "%s-%s" ha fallado; el bloque se muestra sin animación.',
                        blockType, variant, e);
                    revelarSinAnimacion(block);
                }
            };
            // Tiene animación específica de variante — usarla
            if (isAboveFold) {
                // Hero: ejecutar de inmediato (ya visible)
                scheduleAntiInvisibilityRescue(block);
                globalScope.add(() => {
                    block.classList.add('anime-init');
                    runVariant();
                    animateGhostNumbers(block);
                });
            } else {
                // Below fold: CSS oculta section hasta anime-init.
                // IO dispara → variantFn (Anime.js inline styles) → anime-init (CSS revela).
                observeOnce(block, () => {
                    // La red viaja con el bloque: se arma al disparar, no al cargar la página.
                    // ANTES del globalScope.add: si variantFn lanza, anime.js NO captura la
                    // excepción (Scope.execute → `cb(this)` sin try/catch) y se perdería el
                    // rescate del bloque — justo el caso que la red existe para cubrir.
                    scheduleAntiInvisibilityRescue(block);
                    globalScope.add(() => {
                        runVariant();
                        requestAnimationFrame(() => {
                            block.classList.add('anime-init');
                            animateGhostNumbers(block);
                        });
                    });
                });
            }
            return; // Salir del forEach para no ejecutar el fallback genérico
        }

        // --- Fallback genérico (data-anim) — Regla 60-30-10 Cinematográfica ---
        // Sin animación pedida (data-anim="") y sin coreografía de variante → revelar
        // sin animar, como hasta ahora (el 'none' explícito ya salió arriba). Los
        // .stagger-item conservan su stagger scroll-driven por CSS ([data-anim=""]).
        if (!animType) {
            revelarSinEntrada(block);
            return;
        }

        const animateBlockCinematic = (targetBlock) => {
            // Capa 1: Fondo / Estructura (60% de peso visual)
            // Solo aplica blur al fondo si NO hay targets de texto (evita doble blur)
            const bg = targetBlock.querySelector('.background-layer, section');
            const hasTextTargets = targetBlock.querySelector('h1, h2, h3, p, .card, .feature-card');
            if (bg) {
                animate(bg, {
                    scale: [1.02, 1],
                    opacity: [0, 1],
                    ...(hasTextTargets ? {} : { filter: ['blur(6px)', 'blur(0px)'] }),
                    composition: 'none', // no dejar que un 2º write solapado congele la entrada
                    duration: resolveDuration(DURATION.slow),
                    ease: SPRING_PRESETS.cinematic
                });
            }

            // Capa 2: Contenido y Textos (30% de peso visual)
            const textTargets = targetBlock.querySelectorAll(TEXT_TARGETS_SELECTOR);
            if (textTargets.length > 0) {
                let translateYVal = [25, 0];
                let translateXVal = [0, 0];
                if (animType === 'fade-down') translateYVal = [-25, 0];
                if (animType === 'fade-left') translateXVal = [-30, 0];
                if (animType === 'fade-right') translateXVal = [30, 0];
                if (animType === 'zoom-in') { translateYVal = [0, 0]; }

                animate(textTargets, {
                    opacity: [0, 1],
                    translateY: translateYVal,
                    translateX: translateXVal,
                    filter: ['blur(3px)', 'blur(0px)'],
                    composition: 'none', // evita que un write solapado congele un texto (p.ej. el <p> de content)
                    delay: stagger(resolveStagger(45)),
                    duration: resolveDuration(DURATION.normal),
                    ease: SPRING_PRESETS.deluxe
                });
            } else {
                // Fallback si no hay estructuras de texto complejas.
                // Solo el CONTENIDO: la decoración (fondo animado, forma, separador y su hueco,
                // resplandor, <style>) la decide `fondos/decoracion.js`, el mismo predicado que
                // usan `replayBlockEntrance` y la timeline v4. Aquí se filtraba una clase que no
                // existe (`background-layer`), así que el fondo animado se fundía y se desplazaba
                // con el texto.
                const fallbackTargets = Array.from(targetBlock.querySelector('section')?.children || [targetBlock])
                    .filter(el => !esDecoracion(el));
                animate(fallbackTargets, {
                    opacity: [0, 1],
                    translateY: [25, 0],
                    composition: 'none', // idem: un 2º write no debe congelar la entrada
                    duration: resolveDuration(DURATION.normal),
                    ease: SPRING_PRESETS.deluxe
                });
            }

            // Capa 3: Acentos y Acciones (10% de peso visual)
            const actionTargets = targetBlock.querySelectorAll('.btn, .cta-btn, .block-icon-svg, .social-icon');
            if (actionTargets.length > 0) {
                animate(actionTargets, {
                    opacity: [0, 1],
                    scale: [0.85, 1],
                    translateY: [15, 0],
                    composition: 'none', // idem para acentos/acciones (btn, iconos)
                    delay: stagger(resolveStagger(40), { start: 220 }),
                    duration: resolveDuration(DURATION.fast),
                    ease: SPRING_PRESETS.magnetic
                });
            }
        };

        if (isAboveFold) {
            // Above fold: animar de inmediato
            // (rescate ANTES del add: aquí `anime-init` se marca al FINAL del callback, así que
            //  si animateBlockCinematic lanza, el bloque se quedaría sin revelar Y sin red.)
            scheduleAntiInvisibilityRescue(block);
            globalScope.add(() => {
                animateBlockCinematic(block);
                animateGhostNumbers(block);
                block.classList.add('anime-init');
            });
        } else {
            // Below fold: IntersectionObserver para suavidad cinematográfica
            observeOnce(block, () => {
                // La red viaja con el bloque: se arma al disparar, no al cargar la página
                // (antes del add por el mismo motivo: una excepción en la animación no debe
                //  llevarse por delante el rescate del bloque).
                scheduleAntiInvisibilityRescue(block);
                globalScope.add(() => {
                    animateBlockCinematic(block);
                    requestAnimationFrame(() => {
                        block.classList.add('anime-init');
                        animateGhostNumbers(block);
                    });
                });
            });
        }
    });

    // La red anti-invisibilidad cubre DOS fallos distintos, con la MISMA función
    // (scheduleAntiInvisibilityRescue) armada en dos momentos:
    //
    //   1) "animó pero se congeló" → se arma al DISPARAR cada bloque (arriba). Cubre también
    //      a los que animan al hacer scroll, que es la mayoría de la página.
    //   2) "NUNCA animó y el bloque se ve" → se arma aquí, para TODOS. Su IO puede no disparar
    //      jamás: `observeOnce` usa rootMargin '0px 0px -12% 0px', que recorta el 12% inferior
    //      del viewport; si la página cabe entera y no hay scroll posible (subpáginas cortas —
    //      Contacto/Gracias/404 — en monitores grandes, o el footer de una página corta), un
    //      bloque en esa franja NO cruza el umbral y se quedaría invisible para siempre.
    //
    // El revelado va condicionado a isInViewport(), así que este 2º armado NO adelanta nada
    // fuera de pantalla (que es lo que causaba el parpadeo del rescate global de 1.5 s): para
    // un bloque below-fold es un no-op, y cuando el visitante llegue le rescata su 1er armado.
    blocks.forEach(scheduleAntiInvisibilityRescue);

    // Fallback stagger-items: items ya visibles en viewport al cargar quedan en opacity:0
    // por el scroll-driven animation range ya pasado. Marcar como .in-view para forzar visibilidad.
    requestAnimationFrame(() => {
        document.querySelectorAll('.stagger-item').forEach(item => {
            const rect = item.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
                item.classList.add('in-view');
            }
        });
    });
}

// ── Preview de entrada a demanda (EDITOR) ────────────────────────────────────
// initGlobalAnimations está registrado previewOnly → NO corre en modo edición, así que
// tocar "Animación de entrada" en Ajustes de sección no daría ningún feedback visible.
// Esta función reproduce la entrada REAL de UN bloque una sola vez para que el cliente VEA
// su elección. Usa la MISMA selección que initGlobalAnimations (su coreografía de variante
// si la tiene) → el preview coincide con lo que se verá publicado. 'none'/header no tienen
// entrada que reproducir. NO toca el camino publicado (solo se llama desde el editor tras
// renderPage); en .editor-active las secciones ya están visibles, así que animar los
// targets desde opacity 0 da un replay visible sin dejar nada oculto al terminar.
export function replayBlockEntrance(block) {
    if (!block || PREFERS_REDUCED_MOTION) return;
    if (block.getAttribute('data-anim') === 'none') return;   // 'Ninguna' → estático, nada que reproducir
    // Sección real del bloque (o el wrapper si no la hubiera). En .editor-active ya está
    // visible, así que forzamos sus hijos a opacity:0 y los animamos de entrada → replay
    // SIEMPRE visible, sin depender del estado interno de cada variantFn (que asume la
    // secuencia oculto→anime-init del publicado y no rinde igual llamado suelto en edición).
    const section = block.querySelector('section, header, footer') || block;
    // Targets = hijos directos de CONTENIDO. Qué es decoración (fondo animado, forma, separador y
    // su hueco de recorte, resplandor, <style>) lo decide `fondos/decoracion.js`, el mismo
    // predicado que la entrada genérica y la timeline v4: el hueco del recorte, por ejemplo, no se
    // ve y colarlo desplazaría un paso el stagger de lo que sí es contenido.
    const kids = Array.from(section.children).filter(el => !esDecoracion(el));
    const targets = kids.length ? kids : [section];
    targets.forEach(el => { el.style.opacity = '0'; });       // estado inicial forzado (previene flash)
    animate(targets, {
        opacity:    [0, 1],
        translateY: [28, 0],
        filter:     ['blur(4px)', 'blur(0px)'],
        delay:      stagger(resolveStagger(60)),
        duration:   resolveDuration(DURATION.normal),
        ease:       SPRING_PRESETS.deluxe,
    });
    animateGhostNumbers(block);
}

// Auto-registro en el motor de GeneracionWeb (Solo para previsualización)
registerPostRender(initGlobalAnimations, { previewOnly: true });
